import * as React from 'react';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { Dispatch } from 'redux';
import Head from 'next/head';

import { wrapper } from 'ducks';
import { ProductList } from 'components/ProductList/ProductList';
import { BookingWidgetLayout } from 'components/BookingWidgetLayout/BookingWidgetLayout';
import { fetchSettingsServerSide } from 'ducks/server';
import { fetchProductSummariesServerSide, fetchCustomPagesServerSide } from 'ducks/server';
import { withSSR } from 'withSSR';
import { getI18nServerSideProps } from 'i18n';
import { detectLocale } from 'i18n-server';
import { selectLanguageOptions } from 'ducks/server/settings';
import { languageOptionMap } from 'lib/util/i18nHelpers';
import { absoluteUrl } from 'lib/util/absoluetUrl';
import { findPMPLinkForLocale } from 'lib/util/findPMPLinkForLocale';

interface Props {
  host: string;
}

const BookingWidgetTop: NextPage<Props> = ({ host }) => {
  const router = useRouter();

  return (
    <>
      <Head>
        <meta property="og:url" content={`${host}${router.asPath}`} />
        <meta property="og:type" content={'website'} />
        <meta name="twitter:card" content={'summary_large_image'} />
      </Head>
      <BookingWidgetLayout>
        <ProductList />
      </BookingWidgetLayout>
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  async ({ params, req, res, store }) => {
    const apiKey = params?.apiKey as string;

    let locale = await detectLocale(req, res);

    // Fetch settings using detected language
    const dispatch: Dispatch<any> = store.dispatch;
    await dispatch(fetchSettingsServerSide(apiKey, params?.id as string, locale));

    // Verify that the detected language is in the supplier's supported languages. If it isn't,
    // default to the first supported language and refetch settings.
    const langOptions = selectLanguageOptions(store.getState());
    if (langOptions.length > 0 && !langOptions.some((langOpt) => langOpt.iso === locale)) {
      locale = langOptions[0].iso;
      await dispatch(fetchSettingsServerSide(apiKey, params?.id as string, locale));
    }

    await Promise.all([
      dispatch(fetchProductSummariesServerSide(apiKey, locale)),
      dispatch(fetchCustomPagesServerSide(apiKey, locale)),
    ]);

    const i18nProps = await getI18nServerSideProps(locale);

    if (langOptions.length === 1) {
      (req as any).locale = langOptions[0].iso;
    } else {
      const productSummaries = store.getState().server.productSummaries.all;
      if (productSummaries.length > 0) {
        const sourceLanguage = productSummaries[0]?.source_language || 'EN_US';
        (req as any).locale = languageOptionMap[sourceLanguage]?.iso;
      }
    }

    // If there are private marketplaces but we are viewing as booking widget, redirect to a private marketplace.
    const pmpEnabled = !!store.getState().server.settings.all.private_marketplace;
    const pmpLinks = store.getState().server.settings.all.private_marketplace_links;
    if (!pmpEnabled && (pmpLinks ?? []).length > 0) {
      const pmpLink = findPMPLinkForLocale(pmpLinks, locale);
      if (pmpLink) {
        return {
          redirect: {
            destination: pmpLink.url,
            permanent: false,
          },
        };
      }
    }

    // If API key is invalid, return 404
    if (store.getState().server.settings.error.includes('500')) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        host: absoluteUrl(req),
        ...i18nProps,
      },
    };
  }
);

export default withSSR()(BookingWidgetTop);
